import React, { useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useApp } from '../contexts/AppContext'
import { highlightMatches } from '../utils/searchUtils'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiChevronUp, FiChevronDown, FiGlobe, FiHash, FiType } = FiIcons

// 虛擬化相關的常數
const ITEM_HEIGHT = 60
const CONTAINER_HEIGHT = 500
const BUFFER_SIZE = 10

const BankTable = () => {
  const {
    currentLang,
    paginatedBanks,
    searchQuery,
    sortField,
    sortDirection,
    handleSort,
    translations,
    totalCount
  } = useApp()

  const getSortIcon = useCallback((field) => {
    if (sortField !== field) return null
    return sortDirection === 'asc' ? FiChevronUp : FiChevronDown
  }, [sortField, sortDirection])

  // 統一且增強的排序按鈕樣式
  const getSortButtonClasses = useCallback((field) => {
    const isActive = sortField === field
    return `px-6 py-4 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-all duration-300 group ${
      isActive 
        ? 'bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 text-purple-600 dark:text-purple-300 shadow-sm' 
        : 'text-gray-500 dark:text-gray-400 hover:bg-gradient-to-r hover:from-gray-50 hover:to-purple-50 dark:hover:from-gray-700 dark:hover:to-purple-900/20 hover:text-gray-700 dark:hover:text-gray-300 hover:shadow-sm'
    }`
  }, [sortField])

  // 計算是否需要虛擬化（大量數據時）
  const shouldUseVirtualization = totalCount > 100

  // 增強的虛擬化邏輯
  const virtualizedData = useMemo(() => {
    if (!shouldUseVirtualization) return paginatedBanks
    
    // 實現簡化的虛擬化
    const visibleItemsCount = Math.ceil(CONTAINER_HEIGHT / ITEM_HEIGHT)
    const totalItemsToRender = Math.min(paginatedBanks.length, visibleItemsCount + BUFFER_SIZE)
    
    return paginatedBanks.slice(0, totalItemsToRender)
  }, [paginatedBanks, shouldUseVirtualization])

  // 表格行動畫變體
  const rowVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        delay: index * 0.03,
        ease: "easeOut"
      }
    }),
    exit: { opacity: 0, y: -20, transition: { duration: 0.2 } }
  }

  return (
    <div className="w-full">
      {/* Mobile Card View - 移除星星標記 */}
      <div className="block sm:hidden">
        <div className="space-y-3 px-4 py-4">
          <AnimatePresence mode="wait">
            {virtualizedData.map((bank, index) => (
              <motion.div
                key={`${bank.code}-${index}`}
                custom={index}
                variants={rowVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                whileHover={{ scale: 1.02, y: -2 }}
                className="bg-white dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600 p-4 shadow-sm hover:shadow-lg hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-300 backdrop-blur-sm"
              >
                {/* Bank Code Header - 移除星星 */}
                <div className="flex items-center gap-3 mb-3">
                  <motion.div 
                    className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <SafeIcon icon={FiHash} className="w-4 h-4 text-white" />
                  </motion.div>
                  <div className="font-mono text-lg font-bold text-purple-600 dark:text-purple-300">
                    {searchQuery ? (
                      <span 
                        dangerouslySetInnerHTML={{ 
                          __html: highlightMatches(bank.code, searchQuery) 
                        }} 
                      />
                    ) : (
                      bank.code
                    )}
                  </div>
                </div>

                {/* Bank Names - Enhanced */}
                <div className="space-y-3">
                  {/* English Name */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <SafeIcon icon={FiType} className="w-3 h-3 text-gray-400" />
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        {translations.thNameEn[currentLang]}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white leading-relaxed pl-5 break-words">
                      {searchQuery ? (
                        <span 
                          dangerouslySetInnerHTML={{ 
                            __html: highlightMatches(bank.nameEn, searchQuery) 
                          }} 
                        />
                      ) : (
                        bank.nameEn
                      )}
                    </div>
                  </div>

                  {/* Chinese Name */}
                  {bank.nameZh !== '-' && (
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <SafeIcon icon={FiGlobe} className="w-3 h-3 text-gray-400" />
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          {translations.thNameZh[currentLang]}
                        </span>
                      </div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white leading-relaxed pl-5 break-words">
                        {searchQuery ? (
                          <span 
                            dangerouslySetInnerHTML={{ 
                              __html: highlightMatches(bank.nameZh, searchQuery) 
                            }} 
                          />
                        ) : (
                          bank.nameZh
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Desktop Table View - 移除星星標記 */}
      <div className="hidden sm:block overflow-x-auto">
        <div className={shouldUseVirtualization ? `max-h-[${CONTAINER_HEIGHT}px] overflow-y-auto scrollbar-thin scrollbar-thumb-purple-300 dark:scrollbar-thumb-purple-600 scrollbar-track-gray-100 dark:scrollbar-track-gray-800` : ''}>
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50/80 to-purple-50/50 dark:from-gray-700/80 dark:to-purple-900/30 sticky top-0 z-10 backdrop-blur-sm">
              <tr>
                <th 
                  className={getSortButtonClasses('code')}
                  onClick={() => handleSort('code')}
                >
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiHash} className="w-4 h-4 group-hover:text-purple-500 transition-colors duration-200" />
                    <span>{translations.thCode[currentLang]}</span>
                    {getSortIcon('code') && (
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ duration: 0.3, type: "spring" }}
                      >
                        <SafeIcon 
                          icon={getSortIcon('code')} 
                          className="w-4 h-4 text-purple-500"
                        />
                      </motion.div>
                    )}
                  </div>
                </th>
                <th 
                  className={getSortButtonClasses('nameEn')}
                  onClick={() => handleSort('nameEn')}
                >
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiType} className="w-4 h-4 group-hover:text-purple-500 transition-colors duration-200" />
                    <span>{translations.thNameEn[currentLang]}</span>
                    {getSortIcon('nameEn') && (
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ duration: 0.3, type: "spring" }}
                      >
                        <SafeIcon 
                          icon={getSortIcon('nameEn')} 
                          className="w-4 h-4 text-purple-500"
                        />
                      </motion.div>
                    )}
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiGlobe} className="w-4 h-4" />
                    <span>{translations.thNameZh[currentLang]}</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              <AnimatePresence mode="wait">
                {virtualizedData.map((bank, index) => (
                  <motion.tr
                    key={`${bank.code}-${index}`}
                    custom={index}
                    variants={rowVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    whileHover={{ 
                      backgroundColor: "rgba(139, 92, 246, 0.05)",
                      scale: 1.005,
                      transition: { duration: 0.2 }
                    }}
                    className="hover:shadow-sm cursor-pointer transition-all duration-300 group"
                  >
                    <td className="px-6 py-4 text-sm font-mono font-medium text-gray-900 dark:text-white">
                      <div className="flex items-center gap-3">
                        <motion.div 
                          className="w-2 h-2 bg-gradient-to-r from-purple-400 to-blue-500 rounded-full shadow-sm"
                          whileHover={{ scale: 1.5 }}
                          transition={{ duration: 0.2 }}
                        ></motion.div>
                        {searchQuery ? (
                          <span 
                            dangerouslySetInnerHTML={{ 
                              __html: highlightMatches(bank.code, searchQuery) 
                            }} 
                          />
                        ) : (
                          bank.code
                        )}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                      <div className="break-words leading-relaxed max-w-xs group-hover:text-purple-700 dark:group-hover:text-purple-300 transition-colors duration-200">
                        {searchQuery ? (
                          <span 
                            dangerouslySetInnerHTML={{ 
                              __html: highlightMatches(bank.nameEn, searchQuery) 
                            }} 
                          />
                        ) : (
                          bank.nameEn
                        )}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                      <div className="break-words leading-relaxed max-w-xs group-hover:text-purple-700 dark:group-hover:text-purple-300 transition-colors duration-200">
                        {bank.nameZh !== '-' ? (
                          searchQuery ? (
                            <span 
                              dangerouslySetInnerHTML={{ 
                                __html: highlightMatches(bank.nameZh, searchQuery) 
                              }} 
                            />
                          ) : (
                            bank.nameZh
                          )
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default BankTable